import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Skeleton,
  Alert,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Link,
} from '@mui/material';
import {
  SmartToy as AgentIcon,
  Visibility as ViewIcon,
  CheckCircle as HealthyIcon,
  Warning as DegradedIcon,
  Cancel as StoppedIcon,
  Speed as LatencyIcon,
  Close as CloseIcon,
  Timeline as ActivityIcon,
  Settings as ConfigIcon,
  Info as InfoIcon,
  Memory as PerceptionIcon,
  Policy as PolicyIcon,
  PlayArrow as ActionIcon,
  Favorite as HealthIcon,
  Map as PlanningIcon,
  Psychology as IntelligenceIcon,
  Security as SafetyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  fetchAgents,
  fetchAgentStats,
  fetchAgentActivity,
  formatUptime,
  formatRelativeTime,
  getAgentTypeColor,
  getStatusColor,
  EdgeAgent,
  AgentStats,
  AgentActivity,
  AgentType,
} from '../services/agentsApi';

type SortField = 'name' | 'type' | 'status' | 'deviceName' | 'lastActivity';
type SortDirection = 'asc' | 'desc';

const Agents: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<EdgeAgent[]>([]);
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Detail drawer state
  const [selectedAgent, setSelectedAgent] = useState<EdgeAgent | null>(null);
  const [agentActivity, setAgentActivity] = useState<AgentActivity[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setAgentsLoaded(false);
      setError(null);
      const [agentsData, statsData] = await Promise.all([
        fetchAgents(),
        fetchAgentStats(),
      ]);
      setAgents(agentsData);
      setStats(statsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load agents');
    } finally {
      setAgentsLoaded(true);
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAgents = [...agents].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'deviceName':
        comparison = a.deviceName.localeCompare(b.deviceName);
        break;
      case 'lastActivity':
        comparison = new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime();
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const handleViewAgent = async (agent: EdgeAgent) => {
    setSelectedAgent(agent);
    setActivityLoading(true);
    try {
      const activity = await fetchAgentActivity(agent.id);
      setAgentActivity(activity);
    } catch (err) {
      setAgentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setSelectedAgent(null);
    setAgentActivity([]);
  };

  const getTypeIcon = (type: AgentType) => {
    switch (type) {
      case 'Perception':
        return <PerceptionIcon />;
      case 'Policy':
        return <PolicyIcon />;
      case 'Action':
        return <ActionIcon />;
      case 'Health':
        return <HealthIcon />;
      case 'Planning':
        return <PlanningIcon />;
      case 'Intelligence':
        return <IntelligenceIcon />;
      case 'Safety':
        return <SafetyIcon />;
      default:
        return <AgentIcon />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Running':
        return <HealthyIcon color="success" />;
      case 'Degraded':
        return <DegradedIcon color="warning" />;
      case 'Stopped':
      case 'Error':
        return <StoppedIcon color="error" />;
      default:
        return <AgentIcon color="disabled" />;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AgentIcon color="primary" sx={{ fontSize: 36 }} />
            Edge Agents
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600 }}>
            Autonomous processes running on your gateways. Agents perceive, apply safety policies, 
            and trigger actions directly at the edge.
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={loadData}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AgentIcon color="primary" />
                Total Agents
              </Typography>
              <Typography variant="h3" color="primary">
                {stats?.totalAgents || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HealthyIcon color="success" />
                Healthy
              </Typography>
              <Typography variant="h3" color="success.main">
                {stats?.healthyAgents || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DegradedIcon color="warning" />
                Degraded
              </Typography>
              <Typography variant="h3" color="warning.main">
                {stats?.degradedAgents || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LatencyIcon color="info" />
                Avg Latency
              </Typography>
              <Typography variant="h3" color="info.main">
                {stats?.avgDecisionLatency || 0}
                <Typography component="span" variant="h6" color="text.secondary"> ms</Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Agents Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Agent Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'type'}
                    direction={sortField === 'type' ? sortDirection : 'asc'}
                    onClick={() => handleSort('type')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>Mission / Config</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'deviceName'}
                    direction={sortField === 'deviceName' ? sortDirection : 'asc'}
                    onClick={() => handleSort('deviceName')}
                  >
                    Device / Gateway
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? sortDirection : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'lastActivity'}
                    direction={sortField === 'lastActivity' ? sortDirection : 'asc'}
                    onClick={() => handleSort('lastActivity')}
                  >
                    Last Activity
                  </TableSortLabel>
                </TableCell>
                <TableCell>Version</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!agentsLoaded && sortedAgents.length === 0 ? (
                [0, 1, 2].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width={140} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="rounded" width={60} height={24} /></TableCell>
                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                  </TableRow>
                ))
              ) : null}
              {sortedAgents.map((agent) => (
                <TableRow 
                  key={agent.id} 
                  hover 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleViewAgent(agent)}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box 
                        sx={{ 
                          color: getAgentTypeColor(agent.type),
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {getTypeIcon(agent.type)}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2">{agent.name}</Typography>
                        {agent.source && agent.source !== 'mock' && (
                          <Typography variant="caption" color="text.secondary">
                            Source: {agent.source}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={agent.type}
                      size="small"
                      sx={{ 
                        bgcolor: `${getAgentTypeColor(agent.type)}20`,
                        color: getAgentTypeColor(agent.type),
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/configurations');
                      }}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {agent.missionName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{agent.deviceName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {agent.deviceId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(agent.status)}
                      label={agent.status}
                      size="small"
                      color={getStatusColor(agent.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatRelativeTime(agent.lastActivity)}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        display: 'block',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {agent.lastActivityDescription}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {agent.version}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewAgent(agent);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Agent Detail Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedAgent}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 450 } } }}
      >
        {selectedAgent && (
          <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ color: getAgentTypeColor(selectedAgent.type) }}>
                  {getTypeIcon(selectedAgent.type)}
                </Box>
                <Box>
                  <Typography variant="h6">{selectedAgent.name}</Typography>
                  <Chip 
                    label={selectedAgent.type}
                    size="small"
                    sx={{ 
                      bgcolor: `${getAgentTypeColor(selectedAgent.type)}20`,
                      color: getAgentTypeColor(selectedAgent.type),
                    }}
                  />
                </Box>
              </Box>
              <IconButton onClick={handleCloseDrawer}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Basic Info */}
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon fontSize="small" /> Basic Info
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Device / Gateway" secondary={selectedAgent.deviceName} />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Mission / Config" 
                  secondary={
                    <Link 
                      component="button" 
                      onClick={() => navigate('/configurations')}
                      sx={{ textAlign: 'left' }}
                    >
                      {selectedAgent.missionName}
                    </Link>
                  } 
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Version" secondary={selectedAgent.version} />
              </ListItem>
              {selectedAgent.uptime && (
                <ListItem>
                  <ListItemText primary="Uptime" secondary={formatUptime(selectedAgent.uptime)} />
                </ListItem>
              )}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Current State */}
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HealthyIcon fontSize="small" /> Current State
            </Typography>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Chip
                  icon={getStatusIcon(selectedAgent.status)}
                  label={selectedAgent.status}
                  color={getStatusColor(selectedAgent.status)}
                />
              </Box>
              {selectedAgent.avgDecisionLatency !== undefined && selectedAgent.avgDecisionLatency > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Avg decision latency: <strong>{selectedAgent.avgDecisionLatency} ms</strong>
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Recent Activity */}
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ActivityIcon fontSize="small" /> Recent Activity
            </Typography>
            {activityLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense>
                {agentActivity.length > 0 ? (
                  agentActivity.map((activity, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(activity.timestamp).toLocaleTimeString()}
                          </Typography>
                        }
                        secondary={activity.description}
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText secondary="No recent activity" />
                  </ListItem>
                )}
              </List>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Configuration */}
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ConfigIcon fontSize="small" /> Configuration
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {selectedAgent.config ? (
                <>
                  {selectedAgent.type === 'Perception' && selectedAgent.config.modelName && (
                    <>
                      <Typography variant="body2" gutterBottom>
                        <strong>Model:</strong> {selectedAgent.config.modelName}
                      </Typography>
                      {selectedAgent.config.inputStream && (
                        <Typography variant="body2" gutterBottom>
                          <strong>Input:</strong> {selectedAgent.config.inputStream}
                        </Typography>
                      )}
                      {selectedAgent.config.thresholds && (
                        <Typography variant="body2" component="div">
                          <strong>Thresholds:</strong>
                          <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                            {Object.entries(selectedAgent.config.thresholds).map(([key, val]) => (
                              <li key={key}>{key}: {val}</li>
                            ))}
                          </ul>
                        </Typography>
                      )}
                    </>
                  )}
                  {selectedAgent.type === 'Policy' && selectedAgent.config.policyDescription && (
                    <>
                      <Typography variant="body2" gutterBottom>
                        {selectedAgent.config.policyDescription}
                      </Typography>
                      {selectedAgent.config.zones && (
                        <Typography variant="body2" component="div">
                          <strong>Zones:</strong> {selectedAgent.config.zones.join(', ')}
                        </Typography>
                      )}
                    </>
                  )}
                  {selectedAgent.type === 'Action' && (
                    <>
                      {selectedAgent.config.actionDescription && (
                        <Typography variant="body2" gutterBottom>
                          {selectedAgent.config.actionDescription}
                        </Typography>
                      )}
                      {selectedAgent.config.triggers && (
                        <Typography variant="body2" component="div">
                          <strong>Triggers:</strong> {selectedAgent.config.triggers.join(', ')}
                        </Typography>
                      )}
                    </>
                  )}
                  {selectedAgent.config.tickInterval && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Tick Interval:</strong> {selectedAgent.config.tickInterval}s
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Configuration details not available
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Empty State */}
      {agents.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <AgentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No agents yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a mission and deploy it to a device to launch your first edge agents.
          </Typography>
          <Button 
            variant="contained" 
            sx={{ mt: 2 }}
            onClick={() => navigate('/configurations')}
          >
            Create Mission
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default Agents;

